// CreateCampaignModal — controlled dialog used by DiagiaHome + DiagiaCampaigns.
//
// Lightweight: backdrop click closes, Escape closes, form submit calls the
// passed-in onCreate(payload). No external modal library so it stays inside
// the isolated src/diagia/ folder.

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaPlus, FaSpinner } from 'react-icons/fa';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function CreateCampaignModal({ open, onClose, onCreate }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [scope, setScope] = useState('');
  const [description, setDescription] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [emails, setEmails] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const nameInputRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // Reset state on close so a reopened modal isn't pre-filled.
      setName('');
      setScope('');
      setDescription('');
      setEmailDraft('');
      setEmails([]);
      setSubmitting(false);
      setError('');
      return;
    }
    const t = setTimeout(() => nameInputRef.current?.focus(), 60);
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const addEmailFromDraft = () => {
    const trimmed = emailDraft.trim().toLowerCase();
    if (!trimmed) return;
    if (!EMAIL_RE.test(trimmed)) {
      setError(`Adresse invalide : ${trimmed}`);
      return;
    }
    if (emails.includes(trimmed)) {
      setEmailDraft('');
      return;
    }
    setEmails((prev) => [...prev, trimmed]);
    setEmailDraft('');
    setError('');
  };

  const removeEmail = (e) => setEmails((prev) => prev.filter((x) => x !== e));

  const handleEmailKey = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      addEmailFromDraft();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Le nom de la campagne est obligatoire.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onCreate({
        name: name.trim(),
        scope: scope.trim(),
        description: description.trim(),
        inviteEmails: emails,
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || 'Échec de la création');
      setSubmitting(false);
    }
  };

  return (
    <div
      ref={overlayRef}
      className="dia-modal-overlay"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <form className="dia-modal" onSubmit={handleSubmit}>
        <header className="dia-modal-head">
          <div>
            <span className="dia-section-eyebrow">{t('diagia.modal.eyebrow', 'Nouvelle campagne')}</span>
            <h2>{t('diagia.modal.title', "Identifier des cas d'usage IA")}</h2>
          </div>
          <button
            type="button"
            className="dia-icon-btn"
            onClick={onClose}
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </header>

        <div className="dia-modal-body">
          <label className="dia-field">
            <span>{t('diagia.modal.name', 'Nom de la campagne')}</span>
            <input
              ref={nameInputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('diagia.modal.name_ph', 'Ex. Transformation Service Client')}
              required
              maxLength={160}
            />
          </label>

          <label className="dia-field">
            <span>{t('diagia.home.th_scope', 'Périmètre')}</span>
            <input
              type="text"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder={t('diagia.modal.scope_ph', 'Ex. Direction Relation Client')}
              maxLength={160}
            />
          </label>

          <label className="dia-field">
            <span>{t('diagia.modal.desc', 'Description (optionnelle)')}</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('diagia.modal.desc_ph', "Quel est l'objectif de cette campagne ?")}
              rows={3}
              maxLength={1200}
            />
          </label>

          <div className="dia-field">
            <span>{t('diagia.modal.invite', 'Inviter des participants (email)')}</span>
            <div className="dia-email-input">
              {emails.map((e) => (
                <span key={e} className="dia-email-chip">
                  {e}
                  <button
                    type="button"
                    onClick={() => removeEmail(e)}
                    aria-label={`Retirer ${e}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="email"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                onKeyDown={handleEmailKey}
                onBlur={addEmailFromDraft}
                placeholder={emails.length ? '' : 'thomas@exemple.com, puis Entrée'}
              />
            </div>
            <small className="dia-field-hint">
              {t('diagia.modal.invite_hint_pre', 'Validez chaque adresse avec')} <kbd>{t('diagia.diag.key_enter', 'Entrée')}</kbd>. {t('diagia.modal.invite_hint_post', 'Les invitations seront envoyées en phase 3.')}
            </small>
          </div>

          {error && <p className="dia-modal-error">{error}</p>}
        </div>

        <footer className="dia-modal-foot">
          <button
            type="button"
            className="dia-cta dia-cta-ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="dia-cta dia-cta-primary"
            disabled={submitting || !name.trim()}
          >
            {submitting ? <FaSpinner className="dia-spin" /> : <FaPlus />}
            Créer la campagne
          </button>
        </footer>
      </form>
    </div>
  );
}
